import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { produce } from 'immer';
import _ from 'lodash';
import {
  SBadge,
  SIcon,
  SSpinner,
  STable,
  STooltip,
  type SIconName,
} from '@chargebee/sting-react';
import {
  PlayContext,
  PlayObjectProperty,
  useShowPlayDemoData,
} from '@bbkAdminComponents/play/play-context';
import { DatePickerV2 } from '@bbkAdminComponents/reports-filter-picker/reports-filter-picker';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useGetSelectedBrand } from '@bbkAdminRedux/rtkq/brands.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { pricingPagesSlice } from '@bbkAdminRedux/rtkq/pricing-pages.slice';
import {
  reportsSlice,
  ReportType,
  type PricingPageReportByPlay,
  type PricingPageReportByPlayDataPoint,
} from '@bbkAdminRedux/rtkq/reports.slice';

type Props = {
  playId: string;
};

type ColumnField = {
  name: string;
  fieldName: string;
  description: string;
  percentage: boolean;
};

type ColumnFieldsItem = {
  label: string;
  description: string;
  icon: SIconName;
  fields: ColumnField[];
};

type PricingPagePlayComputedMetricsData = PricingPageReportByPlayDataPoint & {
  customer_click_through_rate: string;
  customer_checkout_rate: string;
  overall_customer_conversion_rate: string;
  session_click_through_rate: string;
  session_checkout_rate: string;
  session_conversion_rate: string;
};

function rate(numerator: unknown, denominator: unknown): string {
  const toNum = (v: unknown): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };
  const num = toNum(numerator);
  const den = toNum(denominator);
  const value = den > 0 ? (num / den) * 100 : 0;
  return String(Number.isFinite(value) ? value.toFixed(2) : '0.00');
}

const columnFields: ColumnFieldsItem[] = [
  {
    label: 'User Report',
    description:
      'User level conversion data for the pricing tables in the play.',
    icon: 'shield-user',
    fields: [
      {
        name: 'customers_entered',
        fieldName: 'Customers Entered',
        description: 'Number of customers landing on the Pricing Table.',
        percentage: false,
      },
      {
        name: 'customers_engaged',
        fieldName: 'Customers Engaged',
        description:
          'Customer who engaged with any of the Price Cards (both redirect URLs and Chargebee Checkouts included)',
        percentage: false,
      },
      {
        name: 'customer_click_through_rate',
        fieldName: 'Click Through Rate',
        description: 'Customers engaged by customers entered',
        percentage: true,
      },
      {
        name: 'checkouts_started',
        fieldName: 'Checkouts Started',
        description:
          'Customers who initiated Chargebee hosted checkout from the Pricing Table',
        percentage: false,
      },
      {
        name: 'checkouts_completed',
        fieldName: 'Checkouts Completed',
        description:
          'Customers who completed a Chargebee hosted Checkout session',
        percentage: false,
      },
      {
        name: 'customer_checkout_rate',
        fieldName: 'Checkout Rate',
        description:
          'Customers with completed checkouts by customers with checkout initiation',
        percentage: true,
      },
      {
        name: 'overall_customer_conversion_rate',
        fieldName: 'Overall Conversion Rate',
        description: 'Overall conversion rate for the pricing page',
        percentage: true,
      },
    ],
  },
  {
    label: 'Session Report',
    description:
      'Session level conversion data for the pricing tables in the play. ' +
      '(A user can create multiple sessions within the applied time period.)',
    icon: 'shopping-cart',
    fields: [
      {
        name: 'sessions_rendered',
        fieldName: 'Sessions Rendered',
        description: 'Sessions where the pricing table was rendered',
        percentage: false,
      },
      {
        name: 'sessions_engaged',
        fieldName: 'Sessions w/ engagement',
        description:
          'Sessions with engagements (clicks) on any of the Price Cards',
        percentage: false,
      },
      {
        name: 'session_click_through_rate',
        fieldName: 'Click Through Rate',
        description: 'Sessions with engagements by sessions rendered',
        percentage: true,
      },
      {
        name: 'sessions_checkouts_started',
        fieldName: 'Sessions Checkouts Started',
        description:
          'Sessions with Chargebee hosted checkout initiation (only Chargebee checkout initiations included)',
        percentage: false,
      },
      {
        name: 'sessions_checkouts_completed',
        fieldName: 'Sessions Checkouts Completed',
        description: 'Sessions with Chargebee hosted checkout completions',
        percentage: false,
      },
      {
        name: 'session_checkout_rate',
        fieldName: 'Checkout Rate',
        description:
          'Sessions with checkout completions by sessions with checkout initiation',
        percentage: true,
      },
      {
        name: 'session_conversion_rate',
        fieldName: 'Overall Conversion Rate',
        description: 'Overall conversion rate for sessions',
        percentage: true,
      },
    ],
  },
];

const PlayPricingTableReports: React.FC<Props> = ({ playId }) => {
  // hooks
  const appKey = useSelector(selectAppId);
  const brandId = useGetSelectedBrand(appKey);
  const { getPlayObjectProperty } = useContext(PlayContext);
  const { startDate: start, endDate: end } = useSelectDatePicker();
  const showDemoData = useShowPlayDemoData();

  // slices
  const {
    data,
    isLoading: isReportLoading,
    isFetching: isReportFetching,
  } = reportsSlice.endpoints.getReportV2.useQuery(
    {
      appKey,
      start,
      end,
      report_type_requests: [
        { report_type: ReportType.pricing_page_report_by_play },
      ],
      context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
    },
    {
      skip: !appKey,
      selectFromResult: selectFromResultTyped<PricingPageReportByPlay>,
    }
  );

  const { data: pricingPagesList = [], isLoading: isLoadingPricingPages } =
    pricingPagesSlice.endpoints.getPricingPagesList.useQuery(
      appKey
        ? {
            appKey,
            brand_id: brandId,
            context: showDemoData
              ? BBKRequestContext.lifecycle_reports
              : undefined,
          }
        : skipToken
    );

  // variables
  const playReportData = React.useMemo<
    PricingPagePlayComputedMetricsData[] | null
  >(() => {
    const filteredData = data?.data.filter(
      (reportData) => reportData.play_id === playId
    );

    if (!filteredData?.length) {
      return null;
    }

    return produce(
      filteredData,
      (draftList: PricingPageReportByPlayDataPoint[]) => {
        draftList.forEach((draft) => {
          const computed = draft as PricingPagePlayComputedMetricsData;
          computed.customer_click_through_rate = rate(
            draft.customers_engaged,
            draft.customers_entered
          );
          computed.customer_checkout_rate = rate(
            draft.checkouts_completed,
            draft.checkouts_started
          );
          computed.overall_customer_conversion_rate = rate(
            draft.checkouts_completed,
            draft.customers_entered
          );
          computed.session_click_through_rate = rate(
            draft.sessions_engaged,
            draft.sessions_rendered
          );
          computed.session_checkout_rate = rate(
            draft.sessions_checkouts_completed,
            draft.sessions_checkouts_started
          );
          computed.session_conversion_rate = rate(
            draft.sessions_checkouts_completed,
            draft.sessions_rendered
          );
        });
      }
    ) as PricingPagePlayComputedMetricsData[];
  }, [data, playId]);

  const liveControl = React.useMemo(
    () =>
      getPlayObjectProperty(PlayObjectProperty.LIVE_CONTROL_NODE)?.target
        .target_key ?? undefined,
    [getPlayObjectProperty]
  );

  const liveControlData = React.useMemo(
    () => playReportData?.find((report) => report.target_id === liveControl),
    [playReportData, liveControl]
  );

  const renderTableComponent = (column: ColumnFieldsItem) => {
    return (
      <div className="tw-mb-8 tw-w-full tw-flex-col">
        <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
            <SIcon
              name={column.icon}
              size={18}
              className="tw-text-primary-600"
            />
          </div>
          <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
            {column.label}
          </span>
        </div>
        <div className="tw-mb-4 tw-text-sm tw-font-light tw-text-neutral-600">
          {column.description}
        </div>
        <STable>
          <STable.Head>
            <STable.Row>
              <STable.HeaderCell className="!tw-text-xs">
                Pricing table
              </STable.HeaderCell>
              {column.fields.map((field) => (
                <STable.HeaderCell key={field.name} className="!tw-text-xs">
                  <div className="tw-flex tw-items-center tw-justify-center">
                    <span className="tw-whitespace-normal tw-break-words tw-text-center">
                      {field.fieldName}
                    </span>
                    {field.description ? (
                      <STooltip
                        label={field.description}
                        width="regular"
                        placement="top"
                      >
                        <SIcon
                          name="circle-question-mark"
                          size={14}
                          className="tw-ml-0.5 tw-flex-shrink-0 tw-text-neutral-400"
                        />
                      </STooltip>
                    ) : null}
                  </div>
                </STable.HeaderCell>
              ))}
              <STable.HeaderCell />
            </STable.Row>
          </STable.Head>
          <STable.Body>
            {liveControl && liveControl !== 'none' && (
              <STable.Row key={liveControl}>
                <STable.Cell>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span>
                      {pricingPagesList.find((page) => page.id === liveControl)
                        ?.name || 'Control'}
                    </span>
                    <SBadge
                      mode="dark"
                      size="regular"
                      className="!tw-px-2"
                      variant="success"
                    >
                      Control
                    </SBadge>
                  </div>
                </STable.Cell>
                {column.fields.map((field) => (
                  <STable.Cell key={field.name} className="tw-text-center">
                    {_.get(liveControlData, field.name, 0)}
                    {field.percentage ? '%' : ''}
                  </STable.Cell>
                ))}
                <STable.Cell />
              </STable.Row>
            )}
            {playReportData
              ?.filter((report) => report.target_id !== liveControl)
              .map((report) => (
                <STable.Row key={report.target_id}>
                  <STable.Cell>
                    {pricingPagesList.find(
                      (page) => page.id === report.target_id
                    )?.name || 'N/A'}
                  </STable.Cell>
                  {column.fields.map((field) => (
                    <STable.Cell key={field.name} className="tw-text-center">
                      {_.get(report, field.name, 0)}
                      {field.percentage ? '%' : ''}
                    </STable.Cell>
                  ))}
                  <STable.Cell />
                </STable.Row>
              ))}
          </STable.Body>
        </STable>
      </div>
    );
  };

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-6 tw-px-4">
      <div className="tw-flex tw-w-full tw-justify-end">
        <STooltip
          label="Disabled in demo mode."
          width="regular"
          placement="left"
          enabled={showDemoData}
        >
          <div className="tw-inline-block">
            <DatePickerV2 disabled={showDemoData} />
          </div>
        </STooltip>
      </div>
      {isReportLoading || isLoadingPricingPages || isReportFetching ? (
        <div className="tw-flex tw-min-h-[80vh] tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center">
          <SSpinner
            size="medium"
            testId="default-spinner"
            className="!tw-text-white"
          />
        </div>
      ) : playReportData && playReportData.length > 0 ? (
        <>
          {columnFields.map((column) => (
            <div key={column.label}>{renderTableComponent(column)}</div>
          ))}
        </>
      ) : (
        <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-pt-8">
          <span className="tw-text-neutral-600">No session data found</span>
        </div>
      )}
    </div>
  );
};

export default PlayPricingTableReports;
